// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';

import { ResolvedLabel } from './labels';

import styles from './AnnotationLabelOverlay.module.css';

export interface AnnotationLabelOverlayProps {
  /**
   * Pre-resolved labels (one per visible label).  The resolver in
   * `resolveLabels.ts` handles variable-anchored lookups; the
   * renderer sees only resolved coordinates and so never reaches
   * into engine state.
   */
  labels: ReadonlyArray<ResolvedLabel>;
}

// Geometry constants for the pill background.  We don't have access
// to the rendered text's bounding box at JSX-build time, so we
// estimate the pill width from the text length using a fixed
// character width.  This is approximate but deterministic, which is
// what we need for SSR-friendly, snapshot-stable rendering.  The
// font-size and metric here match the .text rule in
// AnnotationLabelOverlay.module.css.
const ESTIMATED_CHAR_WIDTH = 7;
const PADDING_X = 8;
const PADDING_Y = 4;
const TEXT_HEIGHT = 14;

interface BadgeSize {
  readonly width: number;
  readonly height: number;
}

function badgeSize(text: string): BadgeSize {
  return {
    width: text.length * ESTIMATED_CHAR_WIDTH + PADDING_X * 2,
    height: TEXT_HEIGHT + PADDING_Y * 2,
  };
}

/**
 * D3 acceptance criterion 30 -- annotation label overlay layer.
 *
 * Renders one pill-shaped text badge per resolved label.  Pills are
 * centered on the resolved (cx, cy); the width is estimated from the
 * label text length using a fixed-width heuristic that matches the
 * stylesheet's font-size.  The renderer never measures DOM, never
 * uses refs, and never schedules a re-layout pass -- SSR and
 * snapshot-stable behavior comes for free.
 *
 * Mirrors the C28 / C29 contract:
 *
 *   1. No engine imports.  The resolver is the only file allowed to
 *      know about `ViewElement`; the renderer sees only the
 *      pre-resolved coordinates.  Enforced by the source audit in
 *      [tests/annotation-label-readonly.test.ts](../tests/annotation-label-readonly.test.ts).
 *
 *   2. No callbacks in props.  Pointer events pass through to the
 *      diagram element underneath via `pointer-events: none`.  An
 *      editable / draggable label would need its own component with
 *      an explicit pointer-events policy.
 */
export const AnnotationLabelOverlay = React.memo(function AnnotationLabelOverlay(
  props: AnnotationLabelOverlayProps,
): React.ReactElement | null {
  const { labels } = props;

  if (labels.length === 0) {
    return null;
  }

  return (
    <g className={clsx(styles.overlay, 'simlin-annotation-label-overlay')}>
      {labels.map((label) => {
        const { width, height } = badgeSize(label.text);
        const halfW = width / 2;
        const halfH = height / 2;
        return (
          <g
            key={label.uid}
            className="simlin-annotation-label"
          >
            <rect
              className={clsx(styles.pill, 'simlin-annotation-label-pill')}
              x={label.cx - halfW}
              y={label.cy - halfH}
              width={width}
              height={height}
              rx={4}
              ry={4}
              fill={label.color}
            />
            <text
              className={clsx(styles.text, 'simlin-annotation-label-text')}
              x={label.cx}
              y={label.cy}
            >
              {label.text}
            </text>
          </g>
        );
      })}
    </g>
  );
});
